import { omit } from 'lodash';
import { type IFramework, type TextRenderSnapshot } from '@/framework';
import { type ILayerProps, type Optional } from '@/version-types';
import { Events } from '../constants';
import type { SVGPreviewRequest } from '../types';

/**
 * 更新舞台中的目标图层
 * @param framework - 框架实例
 * @param layer - 图层
 * @param options - 配置项
 * - options.activate: 是否激活图层
 * - options.dataOnly: 是否只更新图层数据
 */
export const updateLayer = (
    framework: IFramework,
    layer: ILayerProps,
    options: { activate?: boolean; dataOnly?: boolean } = { activate: true, dataOnly: false },
) => {
    const { activate, dataOnly } = options;

    framework.service.frame.addOrUpdateLayer(layer, undefined, dataOnly);
    if (activate) framework.service.frame.setActiveLayer(layer.id);
};

/**
 * 写入历史记录
 * @param framework - 框架实例
 * @param prevLayer - 更新前的图层
 * @param nextLayer - 更新后的图层
 * @param funcID - 功能 ID，主要用于埋点统计
 */
export const pushHistory = (framework: IFramework, prevLayer: ILayerProps, nextLayer: ILayerProps, funcID?: string) => {
    const prev = omit(prevLayer, '__isOnTextEditing');
    const next = omit(nextLayer, '__isOnTextEditing');
    framework.service.history.write(prev, next, funcID);
};

/**
 * 更新组图层并写入历史记录
 * @param framework - 框架实例
 * @param groupLayer - 组图层
 * @param children - 子图层
 * @param options - 配置项
 * - options.funcID: 功能 ID，主要用于埋点统计
 * - options.update: 是否更新图层
 * - options.dataOnly: 是否只更新图层数据
 * - options.writeHistory: 是否写入历史记录
 * @returns
 */
export const updateGroupLayer = (
    framework: IFramework,
    groupLayer: ILayerProps,
    children: ILayerProps[],
    options: { funcID?: string; update?: boolean; dataOnly?: boolean; writeHistory?: boolean },
) => {
    if (children.length === 0) return groupLayer;

    const { funcID = '', update = true, dataOnly = false, writeHistory = true } = options;

    const prevChildren = groupLayer.children;

    const nextChildren = prevChildren.map((prev, idx) => {
        const next = children[idx];
        return next ? next : prev;
    });

    let nextGroupLayer: ILayerProps = { ...groupLayer };

    if (nextChildren.length > 0) {
        nextGroupLayer = { ...groupLayer, children: nextChildren };
        if (update) updateLayer(framework, nextGroupLayer, { activate: true, dataOnly });
        if (writeHistory) pushHistory(framework, groupLayer, nextGroupLayer, funcID);
    }

    return nextGroupLayer;
};

/**
 * 向可选的 Text SVG Preview 插件发送连续交互通知，本方法只组装并转发事件，不直接执行排版或绘制。
 *
 * `snapshot` 的三态语义由 `SVGPreviewRequest` 定义：`undefined` 表示交给 Preview 插件执行 layout，
 * `null` 表示已经尝试排版但当前没有可用结果，非空值表示可供本轮构建校验和复用的候选 snapshot。
 *
 * @param framework - 当前编辑器 Framework。
 * @param phase - Preview 会话阶段。
 * @param layer - 本次需要更新或提交的目标图层。
 * @param snapshot - 可选的预解析排版结果；省略时由 Preview 插件自行解析。
 * @returns 无返回值。
 */
export const requestTextSVGPreview = (
    framework: IFramework,
    phase: SVGPreviewRequest['phase'],
    layer: ILayerProps,
    snapshot?: Optional<TextRenderSnapshot>,
): void => {
    framework.service.events.emit(Events.SVGPreviewRequest, {
        phase,
        layer,
        ...(snapshot === undefined ? {} : { snapshot }),
    } satisfies SVGPreviewRequest);
};
